/**
 * FR-030…045 · Chi tiết tên miền — bám bố cục `domain-detail.html`:
 * page-head (tiêu đề + popover chuyển tên miền) · card[data-tabs] với
 * tabs DNS records · Tổng quan · SSL/TLS · Quyền truy cập · Nhật ký.
 *
 * Tab "Quyền truy cập" (FR-043/044) dùng `DomainAccessPanel` của module
 * `permissions` (M5); tab "Nhật ký" (FR-045) dùng `AuditLogPage` ở chế độ nhúng,
 * lọc sẵn theo `domainId` (M6). Hai chỗ nối dây này là việc của mốc M7 — trước đó
 * chúng vẫn là trạng thái "sắp có" dù cả hai màn hình nguồn đã xong.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../lib/api';
import { useT } from '../lib/i18n';
import { DOMAIN_STATUS_CHIP } from '../lib/status';
import { useFormat } from '../lib/format';
import type {
  DnsRecord,
  Domain,
  DomainSyncResult,
  ImportResult,
  VerifyNsResult,
} from '../lib/types';
import { Icon } from '../components/Icon';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SearchBox } from '../components/SearchBox';
import { Pagination } from '../components/Pagination';
import { SortButton } from '../components/SortButton';
import { usePhanTrangUrl } from '../lib/phan-trang';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { Field, SelectField } from '../components/Field';
import { DomainSwitcher } from '../components/DomainSwitcher';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { DeleteDomainModal } from './DomainsPage';
import { DomainAccessPanel } from './permissions';
import { AuditLogPage } from './audit/AuditLogPage';
import { datNhanBreadcrumb } from '../app/breadcrumb';

const TABS = ['dns', 'overview', 'ssl', 'access', 'log'] as const;
type Tab = (typeof TABS)[number];

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'] as const;
const TTL_OPTIONS = [1, 60, 120, 300, 600, 1800, 3600, 7200, 43200, 86400];

/**
 * OQ-259 — hàng lọc dẫn xuất từ `RECORD_TYPES`, không viết tay.
 *
 * Trước đây danh sách này ghi cứng `['all','A','CNAME','MX','TXT']` — bốn trong
 * tám loại hệ thống hỗ trợ. Người có bản ghi `NS`, `SRV`, `CAA`, `AAAA` không
 * lọc được, mà đó lại đúng những loại ít gặp nên khó tìm bằng mắt giữa vài chục
 * dòng.
 *
 * Nối thẳng vào `RECORD_TYPES` để lần thêm loại thứ chín, hàng lọc tự có nó.
 * Hai danh sách song song là hai thứ phải nhớ sửa cùng lúc, và lần này đã quên.
 */
const TYPE_FILTERS = ['all', ...RECORD_TYPES] as const;

/** FR-040 — cửa sổ hoàn tác trước khi gọi API xoá thật. */
const UNDO_WINDOW_MS = 10_000;

/**
 * Trạng thái nghĩa là "Cloudflare CHƯA phục vụ tên miền này" — OQ-122.
 *
 *   pending  vừa thêm, nameserver chưa trỏ về Cloudflare
 *   moved    đã từng chạy rồi nameserver bị trỏ đi nơi khác
 *
 * Hai trạng thái, một hoàn cảnh: vùng DNS tồn tại nhưng không ai hỏi tới nó.
 *
 * ── KHÔNG KHOÁ TAB, CHỈ ĐỔI TAB MẶC ĐỊNH ──────────────────────────────────
 * Bản đầu của thay đổi này khoá `dns` và `ssl`. Số đo bác bỏ: Cloudflare
 * **chấp nhận** `POST` bản ghi DNS vào một zone `pending` — thử thật trên zone
 * của chủ dự án rồi xoá ngay. Và đó là quy trình chuẩn: dựng bản ghi TRƯỚC,
 * chuyển nameserver SAU, để không đứt dịch vụ lúc chuyển.
 *
 * Khoá tab sẽ chặn đúng việc cần làm trong lúc chờ. Nên chỉ đổi tab mặc định
 * sang `overview` — chỗ có nameserver và nút "Kiểm tra lại" — kèm một dòng nói
 * rõ tình trạng. Người dùng vẫn bấm sang DNS records được nếu họ muốn.
 */
const CHUA_PHUC_VU = new Set(['pending', 'moved']);

export function DomainDetailPage() {
  const { formatRelative, formatNumber } = useFormat();
  const t = useT();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const [params, setParams] = useSearchParams();

  const domainQuery = useQuery({
    queryKey: ['domain', id],
    queryFn: () => api.get<Domain>(`/domains/${id}`),
  });
  const domain = domainQuery.data;

  /*
   * Tab mặc định — OQ-122.
   *
   * TÍNH TỪ `domain`, không giữ trong state: trạng thái đến từ một truy vấn,
   * nên `useState` khởi tạo từ nó sẽ chạy khi `domain` còn `undefined` — đúng
   * lỗi OQ-116.
   *
   * Chỉ đổi MẶC ĐỊNH. Có `?tab=` tường minh trong URL thì tôn trọng: người
   * dùng bấm sang DNS records là cố ý, và Cloudflare cho sửa bản ghi ở trạng
   * thái `pending` nên không có lý do chặn.
   */
  const chuaPhucVu = domain ? CHUA_PHUC_VU.has(domain.status) : false;
  const tabTuUrl = params.get('tab') as Tab | null;
  const tab: Tab = tabTuUrl ?? (chuaPhucVu ? 'overview' : 'dns');

  // FR-032 — đổi tiêu đề tab trình duyệt VÀ breadcrumb khi chuyển tên miền.
  // Hai thứ này là một yêu cầu, không phải hai (xem `app/breadcrumb.ts`).
  useEffect(() => {
    if (!domain) return;
    document.title = `${domain.name} · DNS records · DNSHub`;
    datNhanBreadcrumb(domain.name);
  }, [domain?.name]);

  // Rời màn chi tiết thì trả breadcrumb về nhãn suy từ đường dẫn.
  useEffect(() => () => datNhanBreadcrumb(null), []);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['domain', id] });
    await queryClient.invalidateQueries({ queryKey: ['records', id] });
  };
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const sync = useMutation({
    mutationFn: () => api.post<DomainSyncResult>(`/domains/${id}/sync`),
    onSuccess: async (result) => {
      await invalidate();
      toast(
        t('domains.synced', {
          added: result.records.added,
          updated: result.records.updated,
          removed: result.records.removed,
        }),
      );
    },
    onError,
  });

  if (domainQuery.isLoading) return <SkeletonCard rows={8} />;
  if (domainQuery.error || !domain) {
    return (
      <AsyncSection
        loading={false}
        error={domainQuery.error ?? new Error('not found')}
        data={undefined}
        onRetry={() => void domainQuery.refetch()}
      >
        {() => null}
      </AsyncSection>
    );
  }

  const capabilities = domain.capabilities ?? [];
  const allows = (cap: string) => capabilities.includes(cap);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <div className="row" style={{ gap: 10 }}>
            <Link className="btn btn-sm btn-ghost" to="/domains" data-od-id="detail-back">
              {t('detail.back')}
            </Link>
            <span className={`chip ${DOMAIN_STATUS_CHIP[domain.status] ?? ''}`}>
              <span className="dot" />
              {t(`domains.status.${domain.status}`)}
            </span>
            {domain.syncPaused ? (
              <span
                className="chip warn"
                data-od-id="chip-sync-paused"
                title={t('detail.syncPausedHint')}
              >
                <span className="dot" />
                {t('detail.syncPaused')}
              </span>
            ) : null}
            <span className="chip">{domain.plan}</span>
            {domain.zoneId ? (
              <span className="chip mono">
                Zone {domain.zoneId.slice(0, 6)}…{domain.zoneId.slice(-4)}
              </span>
            ) : null}
          </div>

          <h1 style={{ marginTop: 10 }} data-od-id="detail-title">
            <span>{domain.name}</span>
            <DomainSwitcher currentId={domain.id} />
          </h1>

          <p className="lede">
            {domain.lastSyncedAt
              ? t('detail.lede', {
                  when: formatRelative(domain.lastSyncedAt),
                  count: formatNumber(domain.recordCount),
                  owner: domain.owner?.fullName ?? domain.owner?.email ?? '—',
                })
              : t('detail.ledeNoSync', { count: formatNumber(domain.recordCount) })}
          </p>
        </div>

        <div className="row">
          <button
            className="btn"
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            <Icon name="refresh" size={16} />
            {t('domains.sync')}
          </button>
          <DomainToolbar domain={domain} allows={allows} onRefresh={invalidate} />
        </div>
      </div>

      <div className="card" data-tabs data-od-id="detail-tabs">
        <div className="tabs">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              aria-selected={tab === value}
              onClick={() =>
                setParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', value);
                  return next;
                })
              }
            >
              {t(`detail.tab.${value}`)}
            </button>
          ))}
        </div>

        {/*
         * Nói rõ tình trạng — đây mới là thứ chủ dự án cần, chứ không phải
         * khoá tab. Đặt ngay dưới thanh tab nên nhìn thấy ở mọi tab, kể cả
         * khi họ chủ động bấm sang DNS records để dựng bản ghi trước.
         */}
        {chuaPhucVu ? (
          <div className="scope-note warn" style={{ margin: '12px 16px 0' }}>
            <Icon name="warn" size={17} />
            <span>
              {domain.status === 'pending' ? t('detail.lockedPending') : t('detail.lockedMoved')}
            </span>
          </div>
        ) : null}

        <div className="tabpanel" data-panel="dns" hidden={tab !== 'dns'} data-od-id="panel-dns">
          {tab === 'dns' ? (
            <DnsPanel
              nguon={{
                base: `/domains/${domain.id}`,
                id: domain.id,
                ten: domain.name,
                khoaChiTiet: ['domain', domain.id],
              }}
              allows={allows}
            />
          ) : null}
        </div>

        <div
          className="tabpanel"
          data-panel="overview"
          hidden={tab !== 'overview'}
          data-od-id="panel-overview"
        >
          {tab === 'overview' ? (
            <OverviewPanel domain={domain} allows={allows} onRefresh={invalidate} />
          ) : null}
        </div>

        <div className="tabpanel" data-panel="ssl" hidden={tab !== 'ssl'} data-od-id="panel-ssl">
          {tab === 'ssl' ? (
            <SslPanel domain={domain} allows={allows} onRefresh={invalidate} />
          ) : null}
        </div>

        {/* FR-043 / FR-044 — `DomainAccessPanel` (M5) CHÍNH LÀ tabpanel, đã mang
            sẵn class `tabpanel` và `data-od-id="panel-access"`; bọc thêm một lớp
            nữa sẽ nhân đôi `data-od-id`. */}
        {tab === 'access' ? (
          <DomainAccessPanel domainId={domain.id} domainName={domain.name} />
        ) : (
          <div className="tabpanel" data-panel="access" hidden />
        )}

        <div className="tabpanel" data-panel="log" hidden={tab !== 'log'} data-od-id="panel-log">
          {/* FR-045 — nhật ký lọc sẵn theo tên miền hiện tại. */}
          {tab === 'log' ? <AuditLogPage domainId={domain.id} embedded /> : null}
        </div>
      </div>
    </>
  );
}

/* ── Thanh thao tác đầu trang (FR-033) ──────────────────────────────── */

function DomainToolbar({
  domain,
  allows,
  onRefresh,
}: {
  domain: Domain;
  allows: (cap: string) => boolean;
  onRefresh: () => Promise<void>;
}) {
  const t = useT();
  const [dialog, setDialog] = useState<'none' | 'purge' | 'import'>('none');

  return (
    <>
      <button
        className="btn"
        type="button"
        disabled={!allows('zone.cache')}
        title={allows('zone.cache') ? undefined : t('detail.notAllowed')}
        data-od-id="detail-purge"
        onClick={() => setDialog('purge')}
      >
        {t('detail.purge')}
      </button>
      {allows('dns.io') ? (
        <a
          className="btn"
          href={`/api/v1/domains/${domain.id}/export`}
          data-od-id="detail-export"
          download={`${domain.name}.zone`}
        >
          {t('detail.export')}
        </a>
      ) : (
        // Thẻ <a> không có trạng thái khoá — dùng nút khoá kèm tooltip (FR-041).
        <button
          className="btn"
          type="button"
          disabled
          title={t('detail.notAllowed')}
          data-od-id="detail-export"
        >
          {t('detail.export')}
        </button>
      )}
      <button
        className="btn"
        type="button"
        disabled={!allows('dns.io')}
        title={allows('dns.io') ? undefined : t('detail.notAllowed')}
        data-od-id="detail-import"
        onClick={() => setDialog('import')}
      >
        {t('detail.import')}
      </button>

      {dialog === 'purge' ? <PurgeModal domain={domain} onClose={() => setDialog('none')} /> : null}
      {dialog === 'import' ? (
        <ImportModal domain={domain} onClose={() => setDialog('none')} onRefresh={onRefresh} />
      ) : null}
    </>
  );
}

/* ── Tab DNS records (FR-034…041) ───────────────────────────────────── */

/**
 * Nguồn bản ghi DNS cho `DnsPanel` — OQ-320.
 *
 * **Chủ dự án**: *"thay đổi giao diện trang quản lý bản ghi của tên miền con
 * giống như trang quản lý bản ghi của tên miền chính […] không bật tắt được
 * proxy và chỉnh sửa record"*.
 *
 * Máy chủ đã có đủ đường đi từ OQ-291 (`PATCH …/records/:rid`, `…/proxy`); chỉ
 * giao diện là chưa dùng tới. Nên việc cần làm là **cho hai trang dùng chung một
 * bảng**, không phải chép bảng thứ hai.
 *
 * Chép 400 dòng sang trang kia là cách chắc chắn để vài tuần nữa hai bảng lệch
 * nhau — và bên nào cũng trông có lý khi nhìn riêng. Trong phiên này đã trả giá
 * cho đúng chuyện đó bốn lần.
 */
export interface NguonBanGhi {
  /** Đường API gốc: `/domains/:id` hoặc `/subdomains/:id`. */
  base: string;
  /** Dùng dựng khoá cache. */
  id: string;
  /**
   * Tên hiện ở tiêu đề hộp thoại — tên miền, hoặc FQDN của nhánh con.
   *
   * OQ-335: cũng là **phạm vi** để rút gọn tên bản ghi khi mở ô sửa. Hai trang có
   * hai phạm vi khác nhau, và đó chính là chỗ bản cũ sai: trang nhánh con rút gọn
   * theo zone (`test.lyk`) trong khi máy chủ ghép tiếp vào FQDN của nhánh — ra
   * `test.lyk.lyk.thonu.day`.
   */
  ten: string;
  /** Khoá của truy vấn chi tiết, cần làm mới theo mỗi lần đổi bản ghi. */
  khoaChiTiet: unknown[];
}

/**
 * Tên rút gọn để ĐƯA VÀO ô sửa — OQ-335.
 *
 * **Chủ dự án**: *"hiển thị thì luôn hiển thị đầy đủ địa chỉ để nhìn rõ ràng […]
 * khi bấm sửa thì tên chỉ hiển thị test để sửa thôi"*.
 *
 * Bảng hiện FQDN (`record.name`), còn ô sửa hiện phần người dùng thật sự gõ. Hai
 * việc khác nhau nên dùng hai giá trị khác nhau — bản cũ dùng chung `displayName`
 * cho cả hai, và vì thế không thể đúng cả hai.
 *
 * Rút gọn theo **phạm vi đang xem**, không theo zone: ở trang nhánh con
 * `lyk.thonu.day`, bản ghi `test.lyk.thonu.day` phải ra `test` — đúng thứ máy chủ
 * chờ nhận (`tenBanGhiTrongSub` ghép tiếp vào FQDN của nhánh).
 */
export function tenTrongPhamVi(fqdn: string, phamVi: string): string {
  const ten = fqdn.trim().toLowerCase().replace(/\.$/, '');
  const goc = phamVi.trim().toLowerCase().replace(/\.$/, '');
  if (ten === '' || ten === goc) return '@';
  return ten.endsWith(`.${goc}`) ? ten.slice(0, -(goc.length + 1)) : ten;
}

export function DnsPanel({
  nguon,
  allows,
}: {
  nguon: NguonBanGhi;
  allows: (cap: string) => boolean;
}) {
  const { formatNumber } = useFormat();
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState<DnsRecord | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DnsRecord | null>(null);

  /*
   * OQ-263 — 25 dòng như mọi màn khác.
   *
   * `FR-038` từng đòi riêng 50 dòng cho DNS records. Chủ dự án bỏ ngoại lệ ấy:
   * *"tất cả mặc định là 25"*. Một con số cho toàn hệ thống thì người dùng không
   * phải nhớ màn nào khác màn nào; ai cần nhiều hơn thì đổi ở ô chọn và lựa chọn
   * ấy nằm lại trên URL. Tài liệu đã sửa theo.
   */
  const { params: thamSo, page, perPage, datTrang, datSoDong, datLoc } = usePhanTrangUrl();
  const sort = thamSo.get('sort') ?? '';

  const recordsQuery = useQuery({
    queryKey: ['records', nguon.id, { typeFilter, term, page, perPage, sort }],
    queryFn: async () => {
      const search = new URLSearchParams({ page: String(page), perPage: String(perPage) });
      if (typeFilter !== 'all') search.set('type', typeFilter);
      if (term.trim()) search.set('q', term.trim());
      if (sort) search.set('sort', sort);
      const response = await fetch(`/api/v1${nguon.base}/records?${search.toString()}`, {
        credentials: 'include',
      });
      const payload = (await response.json()) as {
        data?: DnsRecord[];
        meta?: { total: number };
        error?: { code: string; message: string };
      };
      if (!response.ok) {
        throw new ApiError(
          response.status,
          payload.error?.code ?? 'INTERNAL_ERROR',
          payload.error?.message ?? t('state.error.body'),
        );
      }
      return { records: payload.data ?? [], total: payload.meta?.total ?? 0 };
    },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['records', nguon.id] });
    await queryClient.invalidateQueries({ queryKey: nguon.khoaChiTiet });
  };
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const toggleProxy = useMutation({
    mutationFn: (payload: { id: string; proxied: boolean }) =>
      api.patch<DnsRecord>(`${nguon.base}/records/${payload.id}/proxy`, {
        proxied: payload.proxied,
      }),
    onSuccess: async (record) => {
      await invalidate();
      toast(
        record.proxied
          ? t('dns.proxyOn', { name: record.name })
          : t('dns.proxyOff', { name: record.name }),
      );
    },
    onError,
  });

  const records = recordsQuery.data?.records ?? [];
  const total = recordsQuery.data?.total ?? 0;

  /*
   * FR-040 — "hoàn tác trong 10 giây bằng toast (soft-undo phía client trước khi
   * gọi API xoá thật)". Bản ghi biến mất khỏi bảng ngay, nhưng lời gọi DELETE
   * chỉ chạy sau 10 giây.
   *
   * Rời tab hay rời trang trong cửa sổ đó thì **xoá thật ngay**, KHÔNG huỷ lặng
   * lẽ: người dùng đã xác nhận và đã được báo "đã xoá"; huỷ âm thầm sẽ làm bản
   * ghi hiện lại ở lần tải sau mà không có lý do nào.
   */
  const pendingDeletes = useRef(new Map<string, number>());
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);

  const commitDelete = useCallback(
    async (id: string) => {
      pendingDeletes.current.delete(id);
      try {
        await api.del<void>(`${nguon.base}/records/${id}`);
      } catch (err) {
        toastError(err instanceof ApiError ? err.message : t('state.error.title'));
      } finally {
        setHiddenIds((prev) => prev.filter((value) => value !== id));
        await invalidateRef.current();
      }
    },
    [nguon.base, t, toastError],
  );

  // `invalidate` được tạo lại mỗi lần render; giữ qua ref để `commitDelete`
  // không phải phụ thuộc vào nó (và không huỷ hẹn giờ theo).
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  const flushRef = useRef<() => void>(() => {});
  flushRef.current = () => {
    for (const [id, timer] of pendingDeletes.current) {
      window.clearTimeout(timer);
      pendingDeletes.current.delete(id);
      void commitDelete(id);
    }
  };
  useEffect(() => () => flushRef.current(), []);

  const scheduleDelete = (record: DnsRecord) => {
    setHiddenIds((prev) => [...prev, record.id]);
    const timer = window.setTimeout(() => void commitDelete(record.id), UNDO_WINDOW_MS);
    pendingDeletes.current.set(record.id, timer);

    toast(t('dns.deletedUndo', { name: record.name }), {
      durationMs: UNDO_WINDOW_MS,
      action: {
        label: t('common.undo'),
        onClick: () => {
          const pending = pendingDeletes.current.get(record.id);
          if (pending === undefined) return; // đã xoá thật rồi
          window.clearTimeout(pending);
          pendingDeletes.current.delete(record.id);
          setHiddenIds((prev) => prev.filter((value) => value !== record.id));
        },
      },
    });
  };

  const visibleRecords = records.filter((record) => !hiddenIds.includes(record.id));

  return (
    <>
      <div className="card-head" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="toolbar grow">
          <div className="filter-pills" data-od-id="dns-filter">
            {TYPE_FILTERS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={typeFilter === value}
                onClick={() => {
                  setTypeFilter(value);
                  datLoc({});
                }}
              >
                {value === 'all' ? t('domains.filterAll') : value}
              </button>
            ))}
          </div>
          <SearchBox
            value={term}
            onChange={(next) => {
              setTerm(next);
              datLoc({});
            }}
            placeholder={t('dns.searchPlaceholder')}
            width={230}
          />
        </div>
        <span className="small muted num">
          {formatNumber(visibleRecords.length)} / {formatNumber(total)}
        </span>
        {allows('dns.create') ? (
          <button
            className="btn btn-sm btn-primary"
            type="button"
            data-od-id="detail-add-record"
            onClick={() => setEditing('new')}
          >
            <Icon name="plus" size={15} />
            {t('detail.addRecord')}
          </button>
        ) : (
          // FR-041: thiếu quyền thì nút Ở TRẠNG THÁI KHOÁ kèm tooltip lý do —
          // khác sidebar (FR-002) vốn ẩn hẳn. Đây là chủ ý của tài liệu.
          <button
            className="btn btn-sm btn-primary"
            type="button"
            data-od-id="detail-add-record"
            disabled
            title={t('detail.notAllowed')}
          >
            <Icon name="plus" size={15} />
            {t('detail.addRecord')}
          </button>
        )}
      </div>

      <AsyncSection
        loading={recordsQuery.isLoading}
        error={recordsQuery.error}
        data={recordsQuery.data}
        onRetry={() => void recordsQuery.refetch()}
        skeleton={<SkeletonCard rows={5} />}
      >
        {() =>
          visibleRecords.length === 0 ? (
            <EmptyState
              icon="dns"
              title={term || typeFilter !== 'all' ? t('dns.emptyFilter') : t('dns.emptyTitle')}
              body={term || typeFilter !== 'all' ? undefined : t('dns.emptyBody')}
            />
          ) : (
            <div className="table-wrap">
              <table className="tbl" id="dns-table" data-od-id="dns-table">
                <thead>
                  <tr>
                    <th>
                      <SortButton
                        label={t('dns.colType')}
                        field="type"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th>
                      <SortButton
                        label={t('dns.colName')}
                        field="name"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th>
                      <SortButton
                        label={t('dns.colContent')}
                        field="content"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th>
                      <SortButton
                        label={t('dns.comment')}
                        field="comment"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th>
                      <SortButton
                        label={t('dns.colProxy')}
                        field="proxied"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th className="right">
                      <SortButton
                        label={t('dns.colTtl')}
                        field="ttl"
                        sort={sort}
                        onSort={(value) => datLoc({ sort: value })}
                      />
                    </th>
                    <th className="right">{t('domains.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRecords.map((record) => (
                    <tr key={record.id} data-state={record.type.toLowerCase()}>
                      <td>
                        <span className="chip mono">{record.type}</span>
                      </td>
                      {/*
                        OQ-335 — bảng hiện tên ĐẦY ĐỦ, không rút gọn.

                        Chủ dự án: *"hiển thị thì luôn hiển thị đầy đủ địa chỉ để
                        nhìn rõ ràng"*. Bản ghi gốc (`@`) vì thế hiện thành chính
                        tên miền, đúng như yêu cầu — `record.name` của nó chính là
                        FQDN của zone.
                      */}
                      <td className="mono">{record.name}</td>
                      <td className="mono">
                        {record.content}
                        {record.priority !== null ? (
                          <span className="muted">
                            {' '}
                            · {t('dns.priorityShort', { n: record.priority })}
                          </span>
                        ) : null}
                      </td>
                      {/*
                       * Ghi chú nội bộ — cùng trường `comment` mà hộp thoại thêm
                       * bản ghi đang nhập vào, chỉ nay đọc được từ bảng.
                       *
                       * `title` giữ nguyên văn: ô này hẹp và ghi chú có thể dài
                       * tới 500 ký tự, nên phần bị cắt phải với tới được bằng
                       * cách rê chuột thay vì phải mở hộp thoại sửa.
                       */}
                      <td className="small" title={record.comment ?? undefined}>
                        {record.comment ? (
                          <span className="ghi-chu">{record.comment}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {record.proxiable ? (
                          <label
                            className="switch"
                            aria-label={`Proxy ${record.name}`}
                            title={allows('dns.proxy') ? undefined : t('detail.notAllowed')}
                          >
                            <input
                              type="checkbox"
                              checked={record.proxied}
                              disabled={!allows('dns.proxy') || toggleProxy.isPending}
                              title={allows('dns.proxy') ? undefined : t('detail.notAllowed')}
                              onChange={(event) =>
                                toggleProxy.mutate({
                                  id: record.id,
                                  proxied: event.target.checked,
                                })
                              }
                            />
                            <span className="track" />
                          </label>
                        ) : (
                          <span className="chip">{t('dns.notProxiable')}</span>
                        )}
                      </td>
                      <td className="right num small">
                        {record.ttl === 1 ? t('dns.ttlAuto') : formatNumber(record.ttl)}
                      </td>
                      <td className="right">
                        <div className="row-actions">
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={!allows('dns.edit')}
                            title={allows('dns.edit') ? undefined : t('detail.notAllowed')}
                            onClick={() => setEditing(record)}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            type="button"
                            disabled={!allows('dns.delete')}
                            title={allows('dns.delete') ? undefined : t('detail.notAllowed')}
                            onClick={() => setDeleting(record)}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </AsyncSection>

      <div className="card-foot">
        <span className="small muted">{t('dns.footNote')}</span>
        <span style={{ flex: 1 }} />
        {/*
          OQ-262 — dùng chung `Pagination` với ba màn danh sách kia.

          Trước đó chỗ này tự dựng hai nút, và dựng sai: chúng mang nhãn "Quay
          lại" / "Mở" (OQ-261) và không có chỉ báo trang. Bốn bản sao là bốn chỗ
          để sai khác nhau.
        */}
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          odId="dns-pager"
          onPage={datTrang}
          onPerPage={datSoDong}
        />
      </div>

      {editing ? (
        <RecordModal
          nguon={nguon}
          record={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            await invalidate();
            const isNew = editing === 'new';
            setEditing(null);
            toast(isNew ? t('dns.created') : t('dns.updated'));
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteRecordModal
          record={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            // FR-040 — xác nhận xong thì ẩn ngay và mở cửa sổ hoàn tác 10 giây;
            // lời gọi DELETE thật chạy sau đó (hoặc ngay khi rời tab).
            scheduleDelete(deleting);
            setDeleting(null);
          }}
        />
      ) : null}
    </>
  );
}

/* ── Tab Tổng quan (FR-033) ─────────────────────────────────────────── */

function OverviewPanel({
  domain,
  allows,
  onRefresh,
}: {
  domain: Domain;
  allows: (cap: string) => boolean;
  onRefresh: () => Promise<void>;
}) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { toast, toastError } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const verifyNs = useMutation({
    mutationFn: () => api.post<VerifyNsResult>(`/domains/${domain.id}/verify-ns`),
    onSuccess: async (result) => {
      await onRefresh();
      if (result.activated) toast(t('domains.nsActive'));
      else toastError(t('domains.nsPending'));
    },
    onError,
  });

  const [hoiTamDung, setHoiTamDung] = useState(false);
  const [hoiNgungDongBo, setHoiNgungDongBo] = useState(false);

  /*
   * Tạm dừng ĐỒNG BỘ — không đụng tới Cloudflare (OQ-233). Mutation riêng chứ
   * không gộp vào `togglePause`: hai thao tác khác hẳn nhau về hậu quả, và gộp
   * lại là mở đường cho một lần bấm nhầm làm cả website mất CDN.
   */
  const toggleSync = useMutation({
    mutationFn: (syncPaused: boolean) => api.patch<Domain>(`/domains/${domain.id}`, { syncPaused }),
    onSuccess: async (updated) => {
      await onRefresh();
      toast(
        updated.syncPaused
          ? t('detail.syncPausedDone', { name: domain.name })
          : t('detail.syncResumedDone', { name: domain.name }),
      );
    },
  });

  const togglePause = useMutation({
    mutationFn: (paused: boolean) => api.patch<Domain>(`/domains/${domain.id}`, { paused }),
    onSuccess: async (updated) => {
      await onRefresh();
      toast(
        updated.paused
          ? t('detail.paused', { name: domain.name })
          : t('detail.resumed', { name: domain.name }),
      );
    },
    onError,
  });

  return (
    <div className="card-body">
      <div className="grid g-side">
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <div className="grow">
                <h2>{t('detail.nameservers')}</h2>
                <div className="sub">
                  {domain.lastSyncedAt ? formatDateTime(domain.lastSyncedAt) : t('common.never')}
                </div>
              </div>
              <span className={`chip ${DOMAIN_STATUS_CHIP[domain.status] ?? ''}`}>
                <span className="dot" />
                {t(`domains.status.${domain.status}`)}
              </span>
            </div>
            <div className="card-body stack">
              {domain.nameServers.length === 0 ? (
                <p className="small muted">{t('common.none')}</p>
              ) : (
                domain.nameServers.map((ns) => (
                  <div className="copy-line" key={ns}>
                    <span className="grow mono">{ns}</span>
                    <button
                      className="btn btn-sm btn-ghost"
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(ns)}
                    >
                      {t('common.copy')}
                    </button>
                  </div>
                ))
              )}
              {allows('zone.ns') ? (
                <div>
                  <button
                    className="btn btn-sm"
                    type="button"
                    onClick={() => verifyNs.mutate()}
                    disabled={verifyNs.isPending}
                  >
                    <Icon name="refresh" size={15} />
                    {t('domains.nsRecheck')}
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {domain.originalNameServers.length > 0 ? (
            <div className="card">
              <div className="card-head">
                <h2>{t('detail.originalNs')}</h2>
              </div>
              <div className="card-body stack">
                {domain.originalNameServers.map((ns) => (
                  <div className="copy-line" key={ns}>
                    <span className="grow mono">{ns}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h2>{t('detail.zoneInfo')}</h2>
            </div>
            <div className="card-body">
              <dl className="kv">
                <dt>{t('detail.zoneId')}</dt>
                <dd className="mono">{domain.zoneId ?? '—'}</dd>
                <dt>{t('detail.owner')}</dt>
                <dd>
                  {domain.owner
                    ? domain.owner.fullName || domain.owner.email
                    : t(`domains.relation.${domain.relation}`)}
                </dd>
                <dt>{t('detail.connection')}</dt>
                <dd>{domain.connection?.name ?? '—'}</dd>
                {/*
                 * Tài khoản Cloudflare sở hữu zone — khác với "Kết nối".
                 *
                 * Một token có thể phủ NHIỀU tài khoản (OQ-117), nên biết đang
                 * dùng kết nối nào chưa đủ để biết zone nằm ở tài khoản nào.
                 * Khi cần vào thẳng dashboard Cloudflare hay hỏi đúng người
                 * giữ tài khoản, đây là dòng trả lời được.
                 */}
                <dt>{t('detail.cfAccount')}</dt>
                <dd>{domain.cfAccountName ?? domain.cfAccountId ?? '—'}</dd>
                {domain.pausedReason ? (
                  <>
                    <dt>{t('detail.pausedReasonLabel')}</dt>
                    <dd data-od-id="paused-reason">
                      {t(`detail.pausedReason.${domain.pausedReason}`)}
                    </dd>
                  </>
                ) : null}
                {domain.syncPaused ? (
                  <>
                    <dt>{t('detail.syncPausedLabel')}</dt>
                    <dd data-od-id="sync-paused-reason">{t('detail.syncPausedHint')}</dd>
                  </>
                ) : null}
                <dt>{t('detail.plan')}</dt>
                <dd>{domain.plan}</dd>
                <dt>{t('detail.createdAt')}</dt>
                <dd className="num">{formatDateTime(domain.createdAt)}</dd>
                <dt>{t('detail.lastSync')}</dt>
                <dd className="num">
                  {domain.lastSyncedAt ? formatDateTime(domain.lastSyncedAt) : t('common.never')}
                </dd>
              </dl>
            </div>
          </div>

          {allows('zone.plan') || allows('g.remove') ? (
            <div className="card">
              <div className="card-head">
                <h2>{t('detail.danger')}</h2>
              </div>
              <div className="card-body stack">
                {allows('zone.plan') ? (
                  <button
                    className="btn btn-block"
                    type="button"
                    data-od-id="btn-toggle-sync"
                    disabled={toggleSync.isPending}
                    onClick={() => {
                      /* Bật lại đồng bộ là thao tác an toàn — không cần hỏi. */
                      if (domain.syncPaused) toggleSync.mutate(false);
                      else setHoiNgungDongBo(true);
                    }}
                  >
                    {domain.syncPaused ? t('detail.syncResume') : t('detail.syncPause')}
                  </button>
                ) : null}

                <ConfirmDialog
                  open={hoiNgungDongBo}
                  busy={toggleSync.isPending}
                  odId="xac-nhan-ngung-dong-bo"
                  title={t('detail.syncPause')}
                  message={t('detail.syncPauseConfirm', { name: domain.name })}
                  undoNote={t('detail.syncPauseUndo')}
                  confirmLabel={t('detail.syncPause')}
                  onCancel={() => setHoiNgungDongBo(false)}
                  onConfirm={() => {
                    setHoiNgungDongBo(false);
                    toggleSync.mutate(true);
                  }}
                />

                {allows('zone.plan') ? (
                  <button
                    className="btn btn-block"
                    type="button"
                    disabled={togglePause.isPending}
                    onClick={() => setHoiTamDung(true)}
                  >
                    {domain.paused ? t('detail.resume') : t('detail.pause')}
                  </button>
                ) : null}

                <ConfirmDialog
                  open={hoiTamDung}
                  danger={!domain.paused}
                  busy={togglePause.isPending}
                  odId="xac-nhan-tam-dung"
                  title={domain.paused ? t('detail.resume') : t('detail.pause')}
                  message={
                    domain.paused
                      ? t('detail.resumeConfirm', { name: domain.name })
                      : t('detail.pauseConfirm', { name: domain.name })
                  }
                  undoNote={domain.paused ? t('detail.resumeUndo') : t('detail.pauseUndo')}
                  confirmLabel={domain.paused ? t('detail.resume') : t('detail.pause')}
                  onCancel={() => setHoiTamDung(false)}
                  onConfirm={() => {
                    setHoiTamDung(false);
                    togglePause.mutate(!domain.paused);
                  }}
                />
                {allows('g.remove') ? (
                  <button
                    className="btn btn-danger btn-block"
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                  >
                    {t('detail.remove')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {confirmDelete ? (
        <DeleteDomainModal
          domain={domain}
          onClose={() => setConfirmDelete(false)}
          onDone={async () => {
            setConfirmDelete(false);
            window.location.assign('/domains');
          }}
        />
      ) : null}
    </div>
  );
}

/* ── Tab SSL/TLS (FR-042) ───────────────────────────────────────────── */

const SSL_MODES = ['off', 'flexible', 'full', 'strict'] as const;

/**
 * Chuẩn hoá giá trị chế độ mã hoá về danh sách hợp lệ — OQ-269.
 *
 * `domain.sslMode` là chuỗi tự do từ API. Một giá trị lạ (Cloudflare thêm chế độ
 * mới, hoặc dữ liệu cũ trong CSDL) sẽ làm `<select>` không khớp option nào và
 * hiện ô trống — trông như mất dữ liệu, trong khi chỉ là chưa biết đọc.
 */
function chuanHoaSsl(v: string | null | undefined): (typeof SSL_MODES)[number] {
  return (SSL_MODES as readonly string[]).includes(v ?? '')
    ? (v as (typeof SSL_MODES)[number])
    : 'full';
}

function SslPanel({
  domain,
  allows,
  onRefresh,
}: {
  domain: Domain;
  allows: (cap: string) => boolean;
  onRefresh: () => Promise<void>;
}) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { toast, toastError } = useToast();
  const editable = allows('zone.ssl');

  const [hstsEnabled, setHstsEnabled] = useState(domain.hsts?.enabled ?? false);
  const [hstsMaxAge, setHstsMaxAge] = useState(String(domain.hsts?.maxAge ?? 15_552_000));
  const [hstsSubdomains, setHstsSubdomains] = useState(domain.hsts?.includeSubdomains ?? false);
  const [hstsPreload, setHstsPreload] = useState(domain.hsts?.preload ?? false);

  /*
   * OQ-270 — MỌI thiết lập trong thẻ này chờ nút "Lưu thay đổi" ở cuối.
   *
   * Chủ dự án: *"chúng ta đã có nút lưu thay đổi là áp dụng cho cả form đó rồi
   * thì thêm nút lưu riêng làm gì nữa"*.
   *
   * Trước đó thẻ này có BA kiểu lưu cùng lúc: chế độ mã hoá có nút Lưu riêng,
   * "Luôn dùng HTTPS" và "TLS tối thiểu" gửi ngay khi chạm, còn HSTS thì chờ nút
   * cuối. Người dùng không có cách nào đoán được cái nào cần bấm — và đó là thứ
   * họ chỉ phát hiện khi một thay đổi lặng lẽ không được lưu.
   *
   * `useState` khởi tạo MỘT LẦN nên phải đồng bộ lại khi dữ liệu từ máy chủ
   * đổi, nếu không ô sẽ giữ giá trị cũ sau khi lưu hoặc sau khi bấm "Đồng bộ
   * Cloudflare".
   */
  const [sslChon, setSslChon] = useState<(typeof SSL_MODES)[number]>(chuanHoaSsl(domain.sslMode));
  const [alwaysHttps, setAlwaysHttps] = useState(domain.alwaysHttps);
  const [minTls, setMinTls] = useState(domain.minTlsVersion ?? '1.2');

  useEffect(() => {
    setSslChon(chuanHoaSsl(domain.sslMode));
    setAlwaysHttps(domain.alwaysHttps);
    setMinTls(domain.minTlsVersion ?? '1.2');
  }, [domain.sslMode, domain.alwaysHttps, domain.minTlsVersion]);

  /* Có gì khác với máy chủ không — nút Lưu mờ khi không có việc để làm. */
  const coThayDoi =
    sslChon !== chuanHoaSsl(domain.sslMode) ||
    alwaysHttps !== domain.alwaysHttps ||
    minTls !== (domain.minTlsVersion ?? '1.2') ||
    hstsEnabled !== (domain.hsts?.enabled ?? false) ||
    Number(hstsMaxAge) !== (domain.hsts?.maxAge ?? 15_552_000) ||
    hstsSubdomains !== (domain.hsts?.includeSubdomains ?? false) ||
    hstsPreload !== (domain.hsts?.preload ?? false);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch<Domain>(`/domains/${domain.id}`, payload),
    onSuccess: async () => {
      await onRefresh();
      toast(t('ssl.saved'));
    },
    onError: (err: unknown) =>
      toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  const expiresIn = useMemo(() => {
    if (!domain.sslExpiresAt) return null;
    const days = Math.round(
      (new Date(domain.sslExpiresAt).getTime() - Date.now()) / (24 * 3600 * 1000),
    );
    return days;
  }, [domain.sslExpiresAt]);

  return (
    <div className="card-body stack">
      <div className="scope-note accent">
        <Icon name="info" size={17} />
        <span>{t('ssl.note')}</span>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="grow">
            <h2>{t('ssl.modeTitle')}</h2>
            <div className="sub">{t('ssl.modeSub')}</div>
          </div>
        </div>
        <div className="card-body">
          {/*
            OQ-269 — MỘT ô chọn thay cho bốn dòng nút "Xác nhận".

            Chủ dự án: *"vì SSL chỉ được chọn 1 loại nên tôi nghĩ chỗ này để
            select box thì đúng hơn"*. Đúng: bốn nút cho một giá trị loại trừ
            nhau là hình thức của **checkbox**, không phải của một lựa chọn duy
            nhất. Mà mỗi nút lại gửi đi ngay, nên không có bước nào để nhìn lại
            trước khi đổi một thiết lập ảnh hưởng toàn bộ lưu lượng HTTPS.

            Nay: ô chọn hiển thị giá trị đang có trên Cloudflare, đổi rồi bấm
            Lưu mới gửi đi. Nút Lưu mờ khi chưa đổi gì — không có việc để làm
            thì không mời người dùng bấm.
          */}
          <div className="setting-row">
            <div className="grow">
              <b>{t('ssl.modeLabel')}</b>
              <div className="sub">{t(`ssl.mode.${sslChon}Sub`)}</div>
            </div>
            <select
              className="lvl"
              aria-label={t('ssl.modeLabel')}
              style={{ width: 150 }}
              value={sslChon}
              disabled={!editable || save.isPending}
              title={editable ? undefined : t('detail.notAllowed')}
              onChange={(event) => setSslChon(event.target.value as (typeof SSL_MODES)[number])}
              data-od-id="ssl-mode-select"
            >
              {SSL_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`ssl.mode.${mode}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-row">
            <div className="grow">
              <b>{t('ssl.alwaysHttps')}</b>
              <div className="sub">{t('ssl.alwaysHttpsSub')}</div>
            </div>
            <label className="switch" aria-label={t('ssl.alwaysHttps')}>
              <input
                type="checkbox"
                checked={alwaysHttps}
                disabled={!editable || save.isPending}
                onChange={(event) => setAlwaysHttps(event.target.checked)}
                data-od-id="ssl-always-https"
              />
              <span className="track" />
            </label>
          </div>

          <div className="setting-row">
            <div className="grow">
              <b>{t('ssl.minTls')}</b>
              <div className="sub">{t('ssl.minTlsSub')}</div>
            </div>
            <select
              aria-label={t('ssl.minTls')}
              style={{ width: 130 }}
              value={minTls}
              disabled={!editable || save.isPending}
              onChange={(event) => setMinTls(event.target.value)}
              data-od-id="ssl-min-tls"
            >
              <option value="1.0">TLS 1.0</option>
              <option value="1.1">TLS 1.1</option>
              <option value="1.2">TLS 1.2</option>
              <option value="1.3">TLS 1.3</option>
            </select>
          </div>

          <div className="setting-row">
            <div className="grow">
              <b>{t('ssl.hsts')}</b>
              <div className="sub">{t('ssl.hstsSub')}</div>
            </div>
            <label className="switch" aria-label={t('ssl.hsts')}>
              <input
                type="checkbox"
                checked={hstsEnabled}
                disabled={!editable}
                onChange={(event) => setHstsEnabled(event.target.checked)}
              />
              <span className="track" />
            </label>
          </div>

          {hstsEnabled ? (
            <div className="grid g-2" style={{ marginTop: 12 }}>
              <Field
                label={t('ssl.hstsMaxAge')}
                type="number"
                value={hstsMaxAge}
                disabled={!editable}
                onChange={(event) => setHstsMaxAge(event.target.value)}
              />
              <div className="stack">
                <label className="row" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    className="check"
                    checked={hstsSubdomains}
                    disabled={!editable}
                    onChange={(event) => setHstsSubdomains(event.target.checked)}
                  />
                  <span className="small">{t('ssl.hstsSubdomains')}</span>
                </label>
                <label className="row" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    className="check"
                    checked={hstsPreload}
                    disabled={!editable}
                    onChange={(event) => setHstsPreload(event.target.checked)}
                  />
                  <span className="small">{t('ssl.hstsPreload')}</span>
                </label>
              </div>
            </div>
          ) : null}

          {editable ? (
            <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={save.isPending || !coThayDoi}
                data-od-id="ssl-save"
                onClick={() =>
                  save.mutate({
                    sslMode: sslChon,
                    alwaysHttps,
                    minTlsVersion: minTls,
                    hsts: {
                      enabled: hstsEnabled,
                      maxAge: Number(hstsMaxAge) || 0,
                      includeSubdomains: hstsSubdomains,
                      preload: hstsPreload,
                    },
                  })
                }
              >
                {save.isPending ? t('common.processing') : t('common.saveChanges')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="grow">
            <h2>{t('ssl.certTitle')}</h2>
          </div>
          {expiresIn !== null ? (
            <span className={`chip ${expiresIn < 15 ? 'warn' : 'ok'}`}>
              <span className="dot" />
              {t('ssl.daysLeft', { n: expiresIn })}
            </span>
          ) : null}
        </div>
        <div className="card-body">
          {domain.sslExpiresAt ? (
            <dl className="kv">
              <dt>{t('ssl.certExpires')}</dt>
              <dd className="num">{formatDateTime(domain.sslExpiresAt)}</dd>
              <dt>{t('detail.zoneId')}</dt>
              <dd className="mono">{domain.zoneId ?? '—'}</dd>
            </dl>
          ) : (
            <p className="small muted">{t('ssl.certNone')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Modal: thêm / sửa bản ghi (FR-035) ─────────────────────────────── */

function RecordModal({
  nguon,
  record,
  onClose,
  onDone,
}: {
  nguon: NguonBanGhi;
  record: DnsRecord | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { formatNumber } = useFormat();
  const t = useT();
  const [type, setType] = useState(record?.type ?? 'A');
  /*
   * OQ-335 — ô sửa nhận tên rút gọn theo PHẠM VI ĐANG XEM, không phải `displayName`.
   *
   * `displayName` do máy chủ rút gọn theo **zone**. Ở trang tên miền hai thứ trùng
   * nhau, nên bản cũ chạy đúng và không ai thấy vấn đề. Ở trang nhánh con thì
   * không: bản ghi `test.lyk.thonu.day` có `displayName = 'test.lyk'`, và máy chủ
   * ghép tiếp vào FQDN của nhánh — ra `test.lyk.lyk.thonu.day`.
   *
   * Nghĩa là mở Sửa rồi bấm Lưu mà KHÔNG đổi gì cũng đủ làm hỏng tên bản ghi. Đã
   * đo lại bằng chính `tenBanGhiTrongSub` trước khi sửa.
   */
  const [name, setName] = useState(record ? tenTrongPhamVi(record.name, nguon.ten) : '');
  const [content, setContent] = useState(record?.content ?? '');
  const [ttl, setTtl] = useState(String(record?.ttl ?? 1));
  const [proxied, setProxied] = useState(record?.proxied ?? false);
  const [priority, setPriority] = useState(String(record?.priority ?? 10));
  const [comment, setComment] = useState(record?.comment ?? '');
  const [data, setData] = useState<Record<string, string>>(() => {
    const source = (record?.data ?? {}) as Record<string, unknown>;
    return {
      service: String(source['service'] ?? ''),
      proto: String(source['proto'] ?? ''),
      weight: String(source['weight'] ?? '1'),
      port: String(source['port'] ?? ''),
      target: String(source['target'] ?? ''),
      flags: String(source['flags'] ?? '0'),
      tag: String(source['tag'] ?? 'issue'),
      value: String(source['value'] ?? ''),
    };
  });
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const proxiable = ['A', 'AAAA', 'CNAME'].includes(type);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        type,
        name,
        content,
        ttl: Number(ttl),
        proxied: proxiable ? proxied : false,
        comment: comment || null,
      };
      if (type === 'MX' || type === 'SRV') body['priority'] = Number(priority);
      if (type === 'SRV') {
        body['data'] = {
          service: data['service'],
          proto: data['proto'],
          weight: Number(data['weight']),
          port: Number(data['port']),
          target: data['target'] || content,
        };
      }
      if (type === 'CAA') {
        body['data'] = {
          flags: Number(data['flags']),
          tag: data['tag'],
          value: data['value'] || content,
        };
      }
      return record
        ? api.patch<DnsRecord>(`${nguon.base}/records/${record.id}`, body)
        : api.post<DnsRecord>(`${nguon.base}/records`, body);
    },
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('dns.recordTitle')}
      subtitle={t('dns.recordSub', { name: nguon.ten })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => save.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? t('common.processing') : t('dns.save')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <div className="grid g-2">
        <SelectField
          label={t('dns.type')}
          value={type}
          onChange={(event) => setType(event.target.value)}
          error={error?.fieldMessage('type')}
        >
          {RECORD_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('dns.ttl')}
          value={proxied && proxiable ? '1' : ttl}
          disabled={proxied && proxiable}
          onChange={(event) => setTtl(event.target.value)}
          error={error?.fieldMessage('ttl')}
        >
          {TTL_OPTIONS.map((value) => (
            <option key={value} value={String(value)}>
              {value === 1 ? t('dns.ttlAuto') : formatNumber(value)}
            </option>
          ))}
        </SelectField>
      </div>

      <Field
        label={t('dns.name')}
        placeholder={t('dns.namePlaceholder')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={error?.fieldMessage('name')}
        required
      />
      <Field
        label={t('dns.content')}
        hint={type === 'CNAME' ? t('dns.contentHint') : undefined}
        value={content}
        onChange={(event) => setContent(event.target.value)}
        error={error?.fieldMessage('content')}
        required
      />

      {type === 'MX' || type === 'SRV' ? (
        <Field
          label={t('dns.priority')}
          type="number"
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
          error={error?.fieldMessage('priority')}
        />
      ) : null}

      {type === 'SRV' ? (
        <div className="grid g-2">
          <Field
            label={t('dns.srvService')}
            placeholder="_sip"
            value={data['service'] ?? ''}
            onChange={(event) => setData({ ...data, service: event.target.value })}
            error={error?.fieldMessage('data.service')}
          />
          <Field
            label={t('dns.srvProto')}
            placeholder="_tcp"
            value={data['proto'] ?? ''}
            onChange={(event) => setData({ ...data, proto: event.target.value })}
            error={error?.fieldMessage('data.proto')}
          />
          <Field
            label={t('dns.srvWeight')}
            type="number"
            value={data['weight'] ?? ''}
            onChange={(event) => setData({ ...data, weight: event.target.value })}
            error={error?.fieldMessage('data.weight')}
          />
          <Field
            label={t('dns.srvPort')}
            type="number"
            value={data['port'] ?? ''}
            onChange={(event) => setData({ ...data, port: event.target.value })}
            error={error?.fieldMessage('data.port')}
          />
        </div>
      ) : null}

      {type === 'CAA' ? (
        <div className="grid g-2">
          <Field
            label={t('dns.caaFlags')}
            type="number"
            value={data['flags'] ?? ''}
            onChange={(event) => setData({ ...data, flags: event.target.value })}
            error={error?.fieldMessage('data.flags')}
          />
          <SelectField
            label={t('dns.caaTag')}
            value={data['tag'] ?? 'issue'}
            onChange={(event) => setData({ ...data, tag: event.target.value })}
            error={error?.fieldMessage('data.tag')}
          >
            <option value="issue">issue</option>
            <option value="issuewild">issuewild</option>
            <option value="iodef">iodef</option>
          </SelectField>
        </div>
      ) : null}

      {proxiable ? (
        <div className="setting-row" style={{ paddingTop: 4 }}>
          <div className="grow">
            <b>{t('dns.proxy')}</b>
            <div className="sub">{t('dns.proxySub')}</div>
          </div>
          <label className="switch" aria-label={t('dns.proxy')}>
            <input
              type="checkbox"
              checked={proxied}
              onChange={(event) => setProxied(event.target.checked)}
            />
            <span className="track" />
          </label>
        </div>
      ) : null}

      <Field
        label={t('dns.comment')}
        placeholder={t('dns.commentPlaceholder')}
        hint={t('dns.commentHint')}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />
    </Modal>
  );
}

/* ── Modal: xoá bản ghi (FR-040) ────────────────────────────────────── */

function DeleteRecordModal({
  record,
  onClose,
  onConfirm,
}: {
  record: DnsRecord;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const t = useT();

  return (
    <Modal
      open
      title={t('dns.deleteTitle')}
      subtitle={t('dns.deleteSub')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" type="button" onClick={onConfirm}>
            {t('common.delete')}
          </button>
        </>
      }
    >
      <div className="scope-note warn">
        <Icon name="warn" size={17} />
        <span>{t('dns.deleteWarn')}</span>
      </div>
      <p className="small">
        {t('dns.deleteConfirmLabel', { type: record.type, name: record.name })}
      </p>
    </Modal>
  );
}

/* ── Modal: xoá cache (FR-033) ──────────────────────────────────────── */

function PurgeModal({ domain, onClose }: { domain: Domain; onClose: () => void }) {
  const t = useT();
  const { toast, toastError } = useToast();
  const [files, setFiles] = useState('');

  const purge = useMutation({
    mutationFn: () => {
      const list = files
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      return api.post<void>(
        `/domains/${domain.id}/purge-cache`,
        list.length > 0 ? { files: list } : { everything: true },
      );
    },
    onSuccess: () => {
      toast(t('detail.purged'));
      onClose();
    },
    onError: (err: unknown) =>
      toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <Modal
      open
      title={t('ssl.purgeTitle')}
      subtitle={domain.name}
      onClose={onClose}

      onSubmit={() => purge.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={purge.isPending}>
            {purge.isPending ? t('common.processing') : t('detail.purge')}
          </button>
        </>
      }
    >
      <div className="scope-note warn">
        <Icon name="warn" size={17} />
        <span>{t('ssl.purgeAllSub')}</span>
      </div>
      <div className="field">
        <label htmlFor="purge-files">{t('ssl.purgeFiles')}</label>
        <textarea
          id="purge-files"
          rows={4}
          className="zone-input"
          value={files}
          onChange={(event) => setFiles(event.target.value)}
          placeholder={`https://${domain.name}/style.css`}
        />
      </div>
    </Modal>
  );
}

/* ── Modal: nhập zone file (FR-039) ─────────────────────────────────── */

function ImportModal({
  domain,
  onClose,
  onRefresh,
}: {
  domain: Domain;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}) {
  const t = useT();
  const { toast, toastError } = useToast();
  const [content, setContent] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const preview = useMutation({
    mutationFn: () =>
      api.post<ImportResult>(`/domains/${domain.id}/import`, { content, mode: 'preview' }),
    onSuccess: setResult,
    onError,
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post<ImportResult>(`/domains/${domain.id}/import`, { content, mode: 'apply' }),
    onSuccess: async (applied) => {
      await onRefresh();
      toast(
        t('import.applied', {
          created: applied.applied.created,
          updated: applied.applied.updated,
        }),
      );
      if (applied.applied.failed.length > 0) {
        toastError(t('import.failed', { count: applied.applied.failed.length }));
      }
      onClose();
    },
    onError,
  });

  return (
    <Modal
      open
      wide
      title={t('import.title')}
      subtitle={t('import.sub')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn"
            type="button"
            disabled={!content.trim() || preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? t('common.processing') : t('import.preview')}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            disabled={!result || apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? t('common.processing') : t('import.apply')}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="zone-content">{t('import.content')}</label>
        <textarea
          id="zone-content"
          className="zone-input"
          rows={8}
          value={content}
          onChange={(event) => {
            setContent(event.target.value);
            setResult(null);
          }}
        />
      </div>

      {result ? (
        <>
          <div className="scope-note">
            <Icon name="info" size={17} />
            <span>
              {t('import.summary', result.plan.summary)}
              {result.plan.skippedLines.length > 0
                ? ` · ${t('import.skippedLines', { count: result.plan.skippedLines.length })}`
                : ''}
            </span>
          </div>
          <div className="import-preview">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('common.confirm')}</th>
                  <th>{t('dns.colType')}</th>
                  <th>{t('dns.colName')}</th>
                  <th>{t('dns.colContent')}</th>
                </tr>
              </thead>
              <tbody>
                {result.plan.entries.map((entry, index) => (
                  <tr key={`${entry.type}-${entry.name}-${index}`}>
                    <td>
                      <span
                        className={`chip ${entry.action === 'create' ? 'ok' : entry.action === 'update' ? 'warn' : ''}`}
                      >
                        {t(`import.action.${entry.action}`)}
                      </span>
                    </td>
                    <td className="mono">{entry.type}</td>
                    <td className="mono">{entry.name}</td>
                    <td className="mono">{entry.content}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
